import {INotification} from '../../features/notifications/models';

// ========================================================================
// LOOKER STUDIO
// ========================================================================

export const looker_studio_base_url = (lookerStudioApiKey: string): string => {
  return `https://lookerstudio.google.com/embed/reporting/${lookerStudioApiKey}/page/hUYBF`;
};

// ========================================================================
// ADMIN
// ========================================================================
export const path_admin_cloud_emailer_email_orders = (orderId: string): string => {
  return `admin/cloud/emailer/email/orders/${orderId}`;
};

export const path_admin_communication_maintenance = (): string => {
  return `admin/communication/maintenance`;
};

export const path_admin_cloud_emailer_email_test = (): string => {
  return `admin/cloud/emailer/email/test`;
};

export const path_admin_functions_orders_instructions_delete_order = (): string => {
  return `admin/functions/orders/instructions/delete_order`;
};

export const path_admin_functions_stock_takes_instructions_delete_stock_take = (): string => {
  return `admin/functions/stock_takes/instructions/delete_stock_take`;
};

export const path_observation_stores_data_storeId_data_api = (storeId: string): string => {
  return `observation/stores_data/${storeId}/data/api`;
};

/** Fixed STOCK_INIT observation document used by `beginStockInitialization`. */
export const STOCK_INIT_OBSERVATION_DOC_ID = 'stock_init';

export const path_observation_stores_data_storeId_data_api_stock_init = (storeId: string): string => {
  return `${path_observation_stores_data_storeId_data_api(storeId)}/${STOCK_INIT_OBSERVATION_DOC_ID}`;
};
export const path_admin_servers_serverId_updater_actions_type = (serverId: string, type: string): string => {
  return `admin/servers/${serverId}/updater_actions/${type}`;
};

// ========================================================================
// HOME
// ========================================================================

export const path_whats_new = (): string => {
  return `whats_new`;
};

// ========================================================================
// NOTIFICATIONS
// ========================================================================

export const path_notifications = (): string => {
  return `notifications`;
};

export const path_notification_id = (notification: INotification): string => {
  return `notifications/${notification.notificationId}`;
};

// ========================================================================
// OBSERVATION
// ========================================================================

// ========================================================================
// OPERATIONAL
// ========================================================================
// TODO These need to move to their own file and be cleaned
export const path_operational_stores_data_storeId_data_api_events_eventId = (storeId: string, eventId: string): string => {
  return `/operational/stores_data/${storeId}/data/api_events/${eventId}`;
};
export const path_operational_stores_data_auto_ordering_auto_orders_GET = (storeId: string): string => {
  return `operational/stores_data/${storeId}/data/auto_ordering/auto_orders/orders`;
};

// Todo check usages if correct
// replaced with PATH_AUTO_ORDER_ORDER_BY_STORE_ID_AND_ORDER_ID
export const path_operational_stores_data_auto_ordering_auto_orders_order_id = (storeId: string, orderId: string): string => {
  return `operational/stores_data/${storeId}/data/auto_ordering/auto_orders/orders/${orderId}`;
};

export const path_operational_stores_data_auto_ordering_auto_orders_order_items_GET = (storeId: string, orderId: string): string => {
  return `operational/stores_data/${storeId}/data/auto_ordering/auto_orders/orders/${orderId}/orderItems`;
};

export const path_operational_stores_data_singular_docs_stock_item_deletion_rules = (storeId: string): string => {
  return `operational/stores_data/${storeId}/data/singular_documents/stock_item_deletion_rules`;
};

export const path_operational_stores_data_storeId_data_auto_ordering_orders_orderId_orderItems = (storeId: string, orderId: string): string => {
  return `/operational/stores_data/${storeId}/data/auto_ordering/auto_orders/orders/${orderId}/orderItems`;
};

export const path_operational_stores_data_storeId_data_auto_ordering_supplier_emails = (storeId: string): string => {
  return `/operational/stores_data/${storeId}/data/auto_ordering/supplier_emails/emails`;
};

export const path_operational_stores_data_storeId_data_auto_ordering_supplier_emails_suppId = (storeId: string, suppId: string): string => {
  return `/operational/stores_data/${storeId}/data/auto_ordering/supplier_emails/emails/${suppId}`;
};

export const path_operational_stores_data_storeId_messages_from_app = (storeId: string): string => {
  return `/operational/stores_data/${storeId}/messages/from_app`;
};

/**
 * Generic path for accessing singular documents in any feature's store data.
 * @param feature - The feature name (e.g., 'operational', 'shared')
 * @param storeId - The unique identifier of the store
 * @param document - The document name
 * @returns Firestore path to the singular document
 */
export const path_feature_stores_data_storeId_singular_documents_document = (
  feature: string,
  storeId: string,
  document: string,
): string => {
  return `${feature}/stores_data/${storeId}/data/singular_documents/${document}`;
};

// ========================================================================
// SERVER_ACCESS
// ========================================================================

// ========================================================================
// SERVERS
// ========================================================================
export const path_servers_serverId_serviceInfo_version = (serverId: string): string => {
  return `servers/${serverId}/service_info/version`;
};

/**
 * Factory builder for server logs paths
 * @param serverId The server ID
 * @param logsType The type of logs (e.g., 'console_logs', 'error_logs')
 * @returns The path to the specified server logs
 */
export const path_servers_serverId_logs_logsType = (serverId: string, logsType: string): string => {
  return `servers/${serverId}/logs/${logsType}`;
};

/**
 * Path to server console logs
 * @param serverId The server ID
 * @returns The path to console logs
 */
export const path_servers_serverId_logs_console = (serverId: string): string => {
  return path_servers_serverId_logs_logsType(serverId, 'console_logs');
};

/**
 * Path to server error logs
 * @param serverId The server ID
 * @returns The path to error logs
 */
export const path_servers_serverId_logs_error = (serverId: string): string => {
  return path_servers_serverId_logs_logsType(serverId, 'error_logs');
};

// ========================================================================
// SHARED
// ========================================================================

// ========================================================================
// STORES
// ========================================================================
export const path_stores = (): string => {
  return `stores`;
};

export const path_stores_storeId_docs_typesense_key = (storeId: string): string => {
  return `stores/${storeId}/docs/typesense_key`;
};

export const path_stores_storeId_settings_advanced_filter = (storeId: string, filterId: string): string => {
  return `stores/${storeId}/settings/advanced_filters/filters/${filterId}`;
};

export const path_stores_storeId_settings_advanced_filter_groups = (storeId: string): string => {
  return `stores/${storeId}/settings/advanced_filters/filters`;
};

export const path_stores_storeId_settings_advanced_filters_meta_sequence = (storeId: string): string => {
  return `stores/${storeId}/settings/advanced_filters/_meta/sequence`;
};

export const path_stores_storeId_settings_advanced_filters_meta_migration = (
  storeId: string,
  generalFilterId: number,
): string => {
  return `stores/${storeId}/settings/advanced_filters/_meta/migration_${generalFilterId}`;
};

export const path_users_userId_settings_date_formatting = (userId: string): string => {
  return `users/${userId}/settings/date_formatting`;
};

export const path_stores_storeId_settings_ao_order_expiration = (storeId: string): string => {
  return `stores/${storeId}/settings/auto_order_expiration`;
};

export const path_stores_storeId_settings_disable_stock_item_rules = (storeId: string): string => {
  return `stores/${storeId}/settings/disable_stock_item_rules`;
};
export const path_stores_storeId_settings_enable_stock_item_rules = (storeId: string): string => {
  return `stores/${storeId}/settings/enable_stock_item_rules`;
};
export const path_stores_storeId_settings_open_order_settings = (storeId: string): string => {
  return `stores/${storeId}/settings/open_order_settings`;
};

export const path_stores_storeId_settings_price_bands = (storeId: string): string => {
  return `stores/${storeId}/settings/price_bands`;
};

export const path_stores_storeId_settings_shelf_talkers_settings = (storeId: string): string => {
  return `stores/${storeId}/settings/shelf_talkers_settings`;
};

export const path_stores_storeId_settings_stock_edit_thresholds = (storeId: string): string => {
  return `stores/${storeId}/settings/stock_edit_thresholds`;
};

export const path_stores_storeId_settings_store_tags = (storeId: string): string => {
  return `stores/${storeId}/settings/store_tags`;
};

export const path_stores_storeId_settings_overrides = (storeId: string): string => {
  return `stores/${storeId}/settings/overrides`;
};

export const path_stores_storeId_settings_add_item_rules = (storeId: string): string => {
  return `stores/${storeId}/settings/add_item_rules`;
};

// ========================================================================
// USER ACCESS
// ========================================================================
export const path_user_access_userId = (userId: string): string => {
  return `/user_access/${userId}`;
};

export const path_user_access_user_id_docs_user_token = (userId: string): string => {
  return `/user_access/${userId}/docs/user_token`;
};

// ========================================================================
// USERS
// ========================================================================
export const path_users_userId = (userId: string): string => {
  return `users/${userId}`;
};

export const path_users_userId_page_alerts_url = (userId: string, url: string): string => {
  return `users/${userId}/page_alerts/${url}`;
};

export const path_users_userId_singular_documents_document = (userId: string, document: string): string => {
  return `users/${userId}/singular_documents/${document}`;
};

export const path_users_userId_settings_fit_to_grid = (userId: string): string => {
  return `users/${userId}/settings/fit_to_grid`;
};

export const path_users_userId_settings_ngp_edits_preview = (userId: string): string => {
  return `users/${userId}/settings/ngp_edits_preview`;
};

export const path_users_userId_settings_stock_edits_preview = (userId: string): string => {
  return `users/${userId}/settings/stock_edits_preview`;
};

export const path_users_userId_settings_stores_storeId_mailer_settings = (userId: string, storeId: string): string => {
  return `users/${userId}/settings/stores/${storeId}/mailer_settings`;
};

export const path_users_userId_settings_stores_storeId_orders_settings = (userId: string, storeId: string): string => {
  return `users/${userId}/settings/stores/${storeId}/order_settings`;
};

export const path_users_userId_settings_table_navigation = (userId: string): string => {
  return `users/${userId}/settings/table_navigation`;
};

export const path_users_userId_settings_stores_storeId_selected_layout = (userId: string, storeId: string): string => {
  return `users/${userId}/settings/stores/${storeId}/selected_layout`;
};

export const path_users_userId_settings_saved_preferences_visible_Fields_feature =
  (userId: string, feature: string): string => {
    return `users/${userId}/settings/saved_preferences/visible_fields/${feature}`;
  };
export const PATH_STORES = (): string => {
  return `stores`;
};
export const PATH_STORES_AT_STORE_ID = (id: string): string => {
  return `stores/${id}`;
};